import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';
import {
  availabilityService,
  Availability,
  AvailabilityStatus,
  STATUS_META,
  STATUS_ORDER,
} from 'api/services/availability.service';

/**
 * Whether you are here.
 *
 * <h3>Why it sits on the account card</h3>
 * The sidebar already draws a line the rest of this file follows: notifications
 * are a SIGNAL and live at the head, the theme is a preference set about once a
 * year and lives in the footer. Availability is neither. It changes several
 * times a day and has to be readable at a glance without being hunted for —
 * and it is a statement about YOU, which is exactly what the account card says.
 * So it belongs on the card, under the name.
 *
 * <h3>Why it can be absent</h3>
 * Conversation routing is off for every organisation until somebody turns it
 * on. A status control for a queue that does not exist is a control that
 * teaches people the app is full of things that do nothing, so this renders
 * nothing at all unless the organisation has opted in.
 */
const AvailabilityControl: React.FC<{ collapsed?: boolean }> = ({ collapsed = false }) => {
  const [enabled, setEnabled] = useState(false);
  const [me, setMe] = useState<Availability | null>(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  // Where to draw the menu in viewport coordinates. The menu is portalled to
  // the body because the account card's text column is `overflow-hidden` — an
  // absolutely positioned menu inside it is clipped to a sliver, which is
  // exactly what happened. A portal has no clipping ancestor to escape.
  const [at, setAt] = useState<{ left: number; bottom: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cfg = await availabilityService.routingConfig();
      if (cancelled) return;
      if (!cfg?.enabled) {
        setEnabled(false);
        return;
      }
      setEnabled(true);
      const mine = await availabilityService.mine();
      if (!cancelled) setMe(mine);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setAt({ left: r.left, bottom: window.innerHeight - r.top + 6 });
  }, [open]);

  // Close on an outside click. Without this the menu survives navigation and
  // hangs over whatever the person went to look at.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      // The menu is no longer a DOM descendant of boxRef, so it has to be
      // asked about separately or clicking an option would close the menu
      // before the option's own handler ran.
      if (boxRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    // A fixed menu does not move with the button, so it is REPOSITIONED on
    // scroll rather than dismissed.
    //
    // Dismissing was the first attempt and it was wrong: a capture-phase scroll
    // listener fires for scroll events from ANY element in the page, and this
    // app scrolls inside <main>, so the menu closed almost as soon as it
    // opened — it was in the DOM one moment and gone the next while the button
    // still read aria-expanded="true".
    const reposition = () => {
      if (!btnRef.current) return;
      const r = btnRef.current.getBoundingClientRect();
      setAt({ left: r.left, bottom: window.innerHeight - r.top + 6 });
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('resize', reposition);
    document.addEventListener('scroll', reposition, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('resize', reposition);
      document.removeEventListener('scroll', reposition, true);
    };
  }, [open]);

  if (!enabled || !me) return null;

  const meta = STATUS_META[me.status] ?? STATUS_META.OFFLINE;

  const choose = async (status: AvailabilityStatus) => {
    setOpen(false);
    if (status === me.status) return;
    const before = me;
    // Optimistic: the dot is the whole point of the control, and a person who
    // taps Available and watches a spinner assumes it did not take.
    setMe({ ...me, status });
    setError(null);
    const res = await availabilityService.setMine({ status });
    if (res.ok) setMe(res.data);
    else {
      setMe(before);
      setError(res.error);
    }
  };

  // Collapsed rail: the dot alone, because a 4rem column has no room for a word
  // and a truncated "Avail…" is worse than a colour everyone learns in a day.
  if (collapsed) {
    return (
      <div className="flex justify-center" title={`${meta.label} — ${meta.help}`}>
        <span className={`h-2.5 w-2.5 rounded-full ${meta.dot}`} aria-label={meta.label} />
      </div>
    );
  }

  return (
    <div ref={boxRef} className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="w-full flex items-center gap-1.5 text-[11px] rounded px-1 py-0.5
                   hover:bg-gray-100 dark:hover:bg-gray-700/50 transition"
        style={{ color: 'var(--text-muted)' }}
        title={meta.help}
      >
        <span className={`h-2 w-2 rounded-full shrink-0 ${meta.dot}`} />
        <span className="truncate">{meta.label}</span>
        {/* The load, but only when there is any. "0 of 3" on an idle morning is
            noise; "2 of 3" is the number that decides whether to take one more. */}
        {me.holding > 0 && (
          <span className="tabular-nums shrink-0">
            {me.holding}/{me.maxConcurrent}
          </span>
        )}
        <ChevronDown className="w-3 h-3 shrink-0 ml-auto" />
      </button>

      {error && (
        <p className="mt-1 text-[10px]" style={{ color: 'var(--danger-text)' }}>
          {error}
        </p>
      )}

      {open && at && createPortal(
        <div
          ref={menuRef}
          role="listbox"
          className="fixed w-56 z-[100] rounded-lg border shadow-lg
                     bg-white dark:bg-gray-800 overflow-hidden"
          style={{ borderColor: 'var(--border)', left: at.left, bottom: at.bottom }}
        >
          {STATUS_ORDER.map((s) => {
            const m = STATUS_META[s];
            const on = s === me.status;
            return (
              <button
                key={s}
                type="button"
                role="option"
                aria-selected={on}
                onClick={() => choose(s)}
                className="w-full text-left px-3 py-2 flex items-start gap-2
                           hover:bg-gray-50 dark:hover:bg-gray-700/50 transition"
              >
                <span className={`h-2 w-2 rounded-full shrink-0 mt-1.5 ${m.dot}`} />
                <span className="min-w-0 flex-1">
                  <span className="block text-[12px] font-medium text-gray-900 dark:text-white">
                    {m.label}
                  </span>
                  {/* What it means for them, not what it sets. */}
                  <span className="block text-[10.5px]" style={{ color: 'var(--text-muted)' }}>
                    {m.help}
                  </span>
                </span>
                {on && <Check className="w-3.5 h-3.5 shrink-0 mt-1 text-emerald-600" />}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
};

export default AvailabilityControl;
